import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import RequiresActionDocumentList from 'components/clinical-data/RequiresActionDocumentList';
import LoaderComponent from 'components/LoaderComponent';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { UserNotificationConsumerRenderProps, withUserNotificationContext } from 'routes/UserNotificationsContext';
import { getAllDoctors } from 'services/doctorServices';
import { getPatientCdrDocumentsThatRequireAction } from 'services/patientCdrDocumentServices';
import { getAllPatientDocumentTypes } from 'services/patientDocumentTypeServices';
import { getUserDisplay } from 'services/userSettingsServices';
import {
  DoctorDto,
  PatientCdrDocumentDto,
  PatientDocumentTypeDto,
  PatientDto,
  UserDisplayDto
} from 'ts-types/api.types';
import { withRouterWorkaround } from 'utils/workaroundUtils';


interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    RouteComponentProps,
    UserNotificationConsumerRenderProps,
    PatientCentralRefDataConsumerRenderProps {

}

const RequiresActionNotificationsModalContent = (props: Props) => {

  const {
    getMessage
  } = props;

  const cancelTokenSource = useRef(axios.CancelToken.source());
  const unmounted = useRef(false);

  const [documents, setDocuments] =
      useState([] as PatientCdrDocumentDto[]);
  const [patientsMap, setPatientsMap] =
      useState({} as { [id: string]: PatientDto });
  const [doctorsMap, setDoctorsMap] =
      useState({} as { [id: string]: DoctorDto });

  const [documentTypesMap, setDocumentTypesMap] =
      useState<{ [code: string]: PatientDocumentTypeDto }>({});

  const [initialDataLoaded, setInitialDataLoaded] =
      useState(false);
  const [errorMessage, setErrorMessage] =
      useState(undefined as string | undefined);
  const [successfulMessage, setSuccessfulMessage] =
      useState(undefined as string | undefined);

  const [currentUserDisplay, setCurrentUserDisplay] =
      useState<UserDisplayDto>({
        id: -1,
        firstName: "",
        name: ""
      });

  const fetchData = async () => {
    setDocuments([]);
    setInitialDataLoaded(false);

    try {

      // fetch document types
      const documentsTypes = await getAllPatientDocumentTypes(
          cancelTokenSource.current);

      // fetch doctors
      const doctors = await getAllDoctors(cancelTokenSource.current);
      let doctorsRecord: Record<string, DoctorDto> = {};
      doctors.forEach((doctor: DoctorDto) => {
        doctorsRecord[doctor.id] = doctor;
      });

      // fetch user data
      const userDisplayDto = await getUserDisplay(cancelTokenSource.current);

      // fetch the documents
      const patientCdrDocumentsThatRequireAction =
          await getPatientCdrDocumentsThatRequireAction(
              false,
              true,
              false,
              false,
              false,
              false,
              undefined,
              undefined,
              cancelTokenSource.current);

      if (!unmounted.current) {
        setDocumentTypesMap(_.keyBy(documentsTypes, "code"));

        setDoctorsMap(doctorsRecord);

        setCurrentUserDisplay(userDisplayDto);

        setDocuments(patientCdrDocumentsThatRequireAction.patientCdrDocuments);
        setPatientsMap(patientCdrDocumentsThatRequireAction.documentPatientMap);
      }

    } catch (e) {
      // ignore
    } finally {
      if (!unmounted.current) {
        setInitialDataLoaded(true);
      }
    }
  }

  useEffect(() => {

    fetchData();

    return (() => {
      unmounted.current = true;
      cancelTokenSource.current && cancelTokenSource.current.cancel();
    })
  }, []);

  if (!initialDataLoaded) {
    return <LoaderComponent message={getMessage("loading")}/>;
  }

  return (
      <RequiresActionDocumentList
          documents={documents}
          documentNotifications={props.documents}
          showNotifications={true}
          patientsMap={patientsMap}
          doctorsMap={doctorsMap}
          documentTypesMap={documentTypesMap}
          currentUserDisplayData={currentUserDisplay}
          setDocuments={(documents: PatientCdrDocumentDto[]) => setDocuments(documents)}
          refreshNotifications={() => props.refreshNotifications()}
          onSuccess={setSuccessfulMessage}
          onError={setErrorMessage}
      />
  );
}

export default withPatientCentralRefDataContext(
    withUserNotificationContext(
        withAuthContext(
            withRouterWorkaround(
                withMessages(RequiresActionNotificationsModalContent)))));
